import { observer } from "mobx-react";
import { InfoTag, InfoTagProps } from "@/components-v2/InfoTag";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { TabProps, Tabs } from "@/components-v2/Tabs";
import {
  Box,
  Card,
  Container,
  ContainerProps,
  Grid,
  GridItem,
  GridItemProps,
  GridProps,
  VBox,
} from "@/components-v2/Box";
import { Button, ButtonProps, IconButton, LinkButton } from "@/components-v2/Button";
import { useTranslation } from "@/stores/RootStoreContext";
import { DataCard, DataCardProps } from "@/components-v2/DataCard";
import { Icon } from "@/components-v2/Icon";
import { DataTable, DataTableProps } from "@/components-v2/DataTable";
import { ReactElement } from "react";
import { immediate } from "@/utils/functions";
import { PersonInfo, ShareIntervalInformation } from "@/types/Shareholder";
import BoardMembers from "@/assets/img/board_members.svg";
import { EmptyState, EmptyStateProps } from "@/components-v2/EmptyState";
import { DCEvent } from "@/events/eventsDomain";
import { TMessage, TMessageParam } from "@/i18n/tt";
import { IconName } from "@/components-v2/iconMapping";
import { InfoButton } from "@/components-v2/InfoButton";
import { TMessageKey } from "@/i18n/types.gen";
import { Alert, AlertProps } from "@/components-v2/Alert";
import { ShareValuationInformation } from "@/common/common";
import {
  Select,
  SelectProps,
  SelectWithNavigator,
  SelectWithNavigatorProps,
} from "@/components-v2/Select";
import { ShareholderStatus } from "@/shareholderRegister/shareholderRegisterDomain";
import { compact, isEmpty } from "lodash";
import { MDateInput } from "@/components-v2/DateInput";
import { LocalDateFieldModel } from "@/common/commonForms";
import {
  CommitmentRow,
  ShareholderCommitmentTotalData,
} from "@/shareholderRegister/commitments/shareholderCommitmentsDomain";
import { ShareholderCommitmentsOverviewTotal } from "@/shareholderRegister/commitments/ShareholderCommitmentsPage";
import { DropdownMenu, DropdownMenuProps } from "@/components-v2/Dropdown";
import { BoxProps } from "@/components-v2/boxHelpers";
import { Transaction } from "@/types/Transactions";
import { ContributionRow } from "@/shareholderRegister/contributions/shareholderContributionsDomain";
import { isReactElement } from "@/utils/reactHelpers";

export type ShareholderDetailsActionButton = {
  type: "button";
  key: string;
  props: ButtonProps;
};

export type ShareholderDetailsActionDropdown = {
  type: "dropdown";
  key: string;
  props: DropdownMenuProps;
};

export type ShareholderDetailsAction =
  | ShareholderDetailsActionButton
  | ShareholderDetailsActionDropdown;

export type ShareholderDetailsHeaderProps = {
  actions?: ShareholderDetailsAction[];
  companySelector?: SelectWithNavigatorProps<{
    orgNo: string;
    name: string;
    shareholderId: number;
  }>;
  companyText?: TMessage;
  infoTags?: InfoTagProps[];
  onClose?: () => void;
  shareholderName?: string;
  shareholderSelector?: SelectWithNavigatorProps<{ id: number; name: string }>;
  shareholderPerDateFilter?: LocalDateFieldModel;
  shareholderStatusFilter?: SelectProps<ShareholderStatus>;
  statusAction?: { icon: IconName; label: TMessage; onClick: () => void };
  statusText?: TMessage;
  tabs?: TabProps[];
  bg?: BoxProps["bg"];
};

export const ShareholderDetailsHeader = observer(
  (props: ShareholderDetailsHeaderProps & Pick<ContainerProps, "maxWidth">) => {
    const { t } = useTranslation();
    return (
      <Box bg={props.bg ?? true} paddingY>
        <Container maxWidth={props.maxWidth ?? "xl"} gap={"lg"}>
          <Box
            className={"items-start flex-col lg:items-center lg:flex-row"}
            justifyContent={"space-between"}
          >
            <VBox gap={"md"}>
              <h3 className={"break-all"}>{props.shareholderName}</h3>
              <Box alignItems={"center"} wrap gap={"lg"} rowGap={"sm"}>
                <Box alignItems={"center"} wrap>
                  {props.infoTags?.map((it) => (
                    <InfoTag {...it} key={it.id} />
                  ))}
                </Box>
                {props.statusText && <span>{t(props.statusText)}</span>}
                {props.statusAction && <LinkButton {...props.statusAction} />}
              </Box>
            </VBox>
            <Box>
              {props.actions?.map((a) => {
                switch (true) {
                  case "type" in a && a.type === "button":
                    return <Button key={a.key} {...a.props} />;
                  case "type" in a && a.type === "dropdown":
                    return <DropdownMenu key={a.key} {...a.props} />;
                }
              })}
            </Box>
          </Box>
        </Container>
      </Box>
    );
  },
);

const ShareholderDetailsAppBar = observer((props: ShareholderDetailsHeaderProps) => {
  const { t } = useTranslation();
  return (
    <VBox
      bg
      padding
      className={"lg:flex-row lg:items-center"}
      justifyContent={"space-between"}
      gap={"md"}
    >
      <Box alignItems={"center"}>
        {props.onClose && <IconButton icon={"x"} tooltip={t("back")} onClick={props.onClose} />}
        {props.shareholderPerDateFilter ? (
          <Box className={"hidden sm:flex sm:w-44"}>
            <MDateInput
              model={props.shareholderPerDateFilter}
              name={"shareholder_details_per_date_filter"}
              placeholder={t("shareholders.filter.per-date.label")}
            />
          </Box>
        ) : props.shareholderStatusFilter ? (
          <Box className={"hidden sm:flex"}>
            <Select {...props.shareholderStatusFilter} iconOnly />
          </Box>
        ) : undefined}
        {props.shareholderSelector && <SelectWithNavigator {...props.shareholderSelector} />}
      </Box>
      <Box gap={"md"}>
        {props.companySelector ? (
          <SelectWithNavigator {...props.companySelector} />
        ) : props.companyText ? (
          t(props.companyText)
        ) : undefined}
      </Box>
    </VBox>
  );
});

export type TabContentProps = ContainerProps & {
  content?: ReactElement;
  emptyState?: EmptyStateProps;
  loading?: boolean;
  alert?: AlertProps;
};

export const TabContent = observer(
  ({ loading, emptyState, content, alert, ...props }: TabContentProps) =>
    loading ? (
      <Container
        padding
        gap={"lg"}
        maxWidth={"xl"}
        grow={"golden"}
        justifyContent={"center"}
        {...props}
      >
        <LoadingIndicator />
      </Container>
    ) : emptyState ? (
      <Container
        padding
        gap={"lg"}
        maxWidth={"xl"}
        grow={"golden"}
        justifyContent={"center"}
        {...props}
      >
        <EmptyState {...emptyState} />
      </Container>
    ) : (
      <Container gap={"lg"} marginY={"lg"} maxWidth={"xl"} {...props}>
        {alert && <Alert {...alert} />}
        {content}
      </Container>
    ),
);

export const ShareholderDetailsLayout = observer(
  ({
    header,
    alertStore,
    content,
  }: {
    header: ShareholderDetailsHeaderProps;
    alertStore: AlertMessageStore;
    content: ReactElement;
  }) => {
    return (
      <FullScreenLayout
        alertStore={alertStore}
        appBar={<ShareholderDetailsAppBar {...header} />}
        centerPanelContainerProps={{ className: "relative" }}
        centerPanel={
          <>
            <ShareholderDetailsHeader {...header} />
            {header.tabs && (
              <Box sticky className={"z-10"} bg borderBottom>
                <Container maxWidth={"xl"}>
                  <Tabs tabs={header.tabs} />
                </Container>
              </Box>
            )}
            {content}
          </>
        }
      />
    );
  },
);

export const ShareholderDetailsLoadingView = () => <TabContent loading />;

export type ShareholdingSectionCard = { colSpan?: GridItemProps["colSpan"] } & (
  | DataCardProps<ShareIntervalInformation>
  | DataCardProps<ShareValuationInformation>
);

export type ShareholdingSectionProps = {
  title: TMessage;
  subtitle?: TMessage;
  icon?: IconName;
  cards: ShareholdingSectionCard[][];
};

const calcColCount = (row: ShareholdingSectionCard[], max: number): GridProps["cols"] =>
  Math.min(
    row.reduce((acc, card) => acc + (card.colSpan === "full" ? 12 : (card.colSpan ?? 1)), 0),
    max,
  ) as GridProps["cols"];

export const ShareholdingSection = observer((props: ShareholdingSectionProps) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"lg"} marginBottom={"lg"}>
      <Box alignItems={"center"} paddingY={"sm"}>
        {props.icon && <Icon name={props.icon} />}
        <VBox>
          <h3>{t(props.title)}</h3>
          {props.subtitle && t(props.subtitle)}
        </VBox>
      </Box>
      {props.cards.map((row, j) => (
        <Grid key={j} cols={1} colsMd={calcColCount(row, 4)} gap={"lg"}>
          {row.map(({ colSpan, ...card }, k) => (
            <GridItem key={k} md={colSpan} className={"flex"}>
              <DataCard {...(card as DataCardProps)} />
            </GridItem>
          ))}
        </Grid>
      ))}
    </VBox>
  );
});

export type ShareholderDetailsOverviewProps = {
  sections: ShareholdingSectionProps[];
  emptyState?: EmptyStateProps;
  alert?: AlertProps;
};

export const ShareholderDetailsOverview = observer((props: ShareholderDetailsOverviewProps) => (
  <TabContent
    emptyState={props.emptyState}
    alert={props.alert}
    content={
      <>
        {props.sections.map((section, i) => (
          <ShareholdingSection key={i} {...section} />
        ))}
      </>
    }
  />
));

export type ShareholderTransactionsProps = {
  transactionsSummaryInfo?: AlertProps;
  transactionsSummaryCards?: DataCardProps[];
  transactionsTable: DataTableProps<Transaction>;
};

export const ShareholderTransactionsView = observer((props: ShareholderTransactionsProps) => {
  return (
    <TabContent
      emptyState={
        isEmpty(props.transactionsTable.tableData)
          ? { illustration: "file-x", title: { key: "no-transactions" } }
          : undefined
      }
      content={
        <>
          {props.transactionsSummaryCards && (
            <>
              <Grid cols={props.transactionsSummaryCards.length as GridProps["cols"]} gap={"lg"}>
                {props.transactionsSummaryCards.map((x, i) => (
                  <DataCard key={i} {...x} />
                ))}
              </Grid>
              {props.transactionsSummaryInfo && <Alert {...props.transactionsSummaryInfo} />}
            </>
          )}
          <DataTable {...props.transactionsTable} />
        </>
      }
    />
  );
});

export type DetailsFieldLink = { type: "link"; url: string; text?: string };

const isDetailsFieldLink = (value: DetailsField[1]): value is DetailsFieldLink =>
  typeof value === "object" && "type" in value && value.type === "link";

type DetailsFieldAction = {
  label: TMessage;
  onClick: DCEvent[] | (() => void);
  icon?: IconName;
};

type DetailsFieldInfo = { info: TMessage };

export type DetailsField = [
  label: TMessage | TMessageKey,
  value:
    | TMessage
    | TMessageParam
    | string
    | number
    | undefined
    | Array<TMessage | TMessageParam | string>
    | DetailsFieldLink
    | ReactElement,
  action?: DetailsFieldAction | DetailsFieldInfo,
];

export type DetailsHeading = { heading: TMessage };

export type DetailsCardProps = {
  title?: TMessage;
  action?: DetailsFieldAction;
  fields: (DetailsField | DetailsHeading)[];
  className?: string;
};

const isDetailsHeading = (field: DetailsField | DetailsHeading): field is DetailsHeading =>
  "heading" in field;

export const DetailsFields = observer(({ fields }: Pick<DetailsCardProps, "fields">) => {
  const { t } = useTranslation();
  return (
    <Grid cols={3} columnGap={"lg"}>
      {fields.map((field, i) =>
        isDetailsHeading(field) ? (
          <GridItem key={i} colSpan={"full"}>
            <strong>{t(field.heading)}</strong>
          </GridItem>
        ) : (
          <DetailsFieldRow key={i} field={field} />
        ),
      )}
    </Grid>
  );
});

const DetailsFieldRow = observer(({ field: [label, value, action] }: { field: DetailsField }) => {
  const { t, tf } = useTranslation();
  return (
    <>
      <GridItem className="wrap-break-word">{t(label)}</GridItem>
      <GridItem colSpan={action ? 1 : 2} className={"wrap-break-word"}>
        {immediate(() => {
          switch (true) {
            case !value:
              return <div className={"subtle"}>{t("not-set")}</div>;
            case isDetailsFieldLink(value):
              return (
                <a href={value.url} target={"_blank"}>
                  {value.text ?? value.url}
                </a>
              );
            case Array.isArray(value):
              return value.map((v, i) => <div key={i}>{tf(v)}</div>);
            case isReactElement(value):
              return value;
            default:
              return tf(value);
          }
        })}
      </GridItem>
      {action && (
        <GridItem justifySelf={"end"}>
          {"info" in action ? (
            <InfoButton infoText={action.info} />
          ) : action.icon ? (
            <IconButton icon={action.icon} tooltip={action.label} onClick={action.onClick} />
          ) : (
            <LinkButton {...action} />
          )}
        </GridItem>
      )}
    </>
  );
});

export const DetailsCard = observer(({ title, action, fields, className }: DetailsCardProps) => {
  return (
    <Card
      title={title}
      action={action && <LinkButton {...action} />}
      className={className}
      gap={"md"}
    >
      <DetailsFields fields={fields} />
    </Card>
  );
});

export type ShareholderDetailsProps = {
  headerElement?: ReactElement;
  detailsCards: (DetailsCardProps | ShareholdingSectionCard)[];
};

export const ShareholderDetailsView = observer((props: ShareholderDetailsProps) => {
  return (
    <TabContent
      content={
        <>
          {props.headerElement}
          {props.detailsCards.map((props, i) =>
            "fields" in props ? (
              <DetailsCard key={i} {...props} />
            ) : (
              <DataCard key={i} {...(props as DataCardProps)} />
            ),
          )}
        </>
      }
    />
  );
});

export type ShareholderAdditionalRepresentativesViewProps = {
  additionalRepresentativesTable: DataTableProps<PersonInfo>;
  inviteAdditionalRepresentative?: () => void;
  isEmpty?: boolean;
};

export const ShareholderAdditionalRepresentativesView = observer(
  (props: ShareholderAdditionalRepresentativesViewProps) => {
    const { t } = useTranslation();
    return (
      <TabContent
        emptyState={
          props.isEmpty
            ? {
                illustration: (
                  <img
                    src={BoardMembers}
                    alt={t("shareholders.no-additional-representatives.title")}
                  />
                ),
                title: { key: "shareholders.no-additional-representatives.title" },
                description: { key: "shareholders.no-additional-representatives.description" },
                actions: compact([
                  props.inviteAdditionalRepresentative && {
                    type: "button",
                    key: "invite",
                    props: {
                      variant: "secondary",
                      label: { key: "shareholders.no-additional-representatives.invite" },
                      onClick: props.inviteAdditionalRepresentative,
                    },
                  },
                ]),
              }
            : undefined
        }
        content={
          <>
            {props.inviteAdditionalRepresentative && (
              <Box justifyContent={"end"}>
                <Button
                  variant={"secondary"}
                  label={{ key: "shareholders.no-additional-representatives.invite" }}
                  onClick={props.inviteAdditionalRepresentative}
                />
              </Box>
            )}
            <DataTable {...props.additionalRepresentativesTable} />{" "}
          </>
        }
      />
    );
  },
);

export type ShareholderCommitmentsViewProps = {
  total: ShareholderCommitmentTotalData;
  table: DataTableProps<CommitmentRow>;
  emptyState?: EmptyStateProps;
};

export const ShareholderCommitmentsView = observer((props: ShareholderCommitmentsViewProps) => {
  return (
    <TabContent
      emptyState={props.emptyState}
      content={
        <>
          <ShareholderCommitmentsOverviewTotal total={props.total} />
          <DataTable {...props.table} />
        </>
      }
    />
  );
});

export type ShareholderContributionsViewProps = {
  total: [key: string, DataCardProps][];
  table: DataTableProps<ContributionRow>;
  emptyState?: EmptyStateProps;
};

export const ShareholderContributionsView = observer((props: ShareholderContributionsViewProps) => {
  return (
    <TabContent
      emptyState={props.emptyState}
      content={
        <>
          <Grid cols={props.total.length}>
            {props.total.map(([key, props]) => (
              <DataCard key={key} {...props} />
            ))}
          </Grid>
          <DataTable {...props.table} />
        </>
      }
    />
  );
});
